<template>
    <div class="container form-container p-4">
        <div class="text-3xl mb-4 pb-2 font-light flex items-end justify-between">
            <div data-cy="user-settings-general-title">{{ $t('user.general.title') }}</div>
            <a-button data-cy="submit-button" class="button-submit" @click="handleSubmit">
                {{ $t('user.general.submit') }}
            </a-button>
        </div>
        <form @change="isFormDirty" @submit.prevent="handleSubmit" data-cy="form-container">
            <p class="text-center text-default-red p-2" v-if="validation.message">
                {{ validation.message }}
            </p>
            <div class="form-section" data-cy="section-general">
                <div class="section-body">
                    <div class="input-container mb-2 pr-4 md:w-1/2" data-cy="user-settings-general-name">
                        <ui-label>
                            {{ $t('user.forms.main.name') }}
                        </ui-label>
                        <ui-input :disabled="true" v-model="v$.user.name.$model" />
                    </div>
                    <div class="input-container mb-2 pr-4 md:w-1/2" data-cy="user-settings-general-email">
                        <ui-label>
                            {{ $t('user.forms.main.email') }}
                        </ui-label>
                        <ui-input :disabled="true" v-model="v$.user.email.$model" />
                    </div>
                </div>
                <div class="section-body">
                    <div class="input-container mb-2 pr-4 lg:w-1/4 md:w-2/5" data-cy="user-settings-general-timezone">
                        <ui-label>
                            {{ $t('user.forms.main.timezone') }}
                        </ui-label>
                        <ui-select
                            :class="{ 'form-error': v$.user.timezone.$error }"
                            v-model="v$.user.timezone.$model"
                            :options="timezones"
                            :searchable="true"
                        />
                        <div
                            data-cy="field-general-timezone-error"
                            :class="{ 'text-default-red': v$.user.timezone.$error }"
                            v-if="v$.user.timezone.required.$invalid && v$.user.timezone.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors.timezone" v-if="validation.errors" />
                    </div>
                    <div class="input-container mb-2 pr-4 lg:w-1/4 md:w-1/5" data-cy="user-settings-general-locale">
                        <ui-label>
                            {{ $t('user.forms.main.locale') }}
                        </ui-label>
                        <ui-select
                            :class="{ 'form-error': v$.user.locale.$error }"
                            v-model="v$.user.locale.$model"
                            :options="locales"
                            :searchable="true"
                        />
                        <div
                            data-cy="field-general-locale-error"
                            :class="{ 'text-default-red': v$.user.locale.$error }"
                            v-if="v$.user.locale.required.$invalid && v$.user.locale.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors.locale" v-if="validation.errors" />
                    </div>
                    <div
                        class="input-container mb-2 pr-4 lg:w-1/4 md:w-1/5"
                        data-cy="user-settings-general-expiry_date"
                    >
                        <ui-label>
                            {{ $t('user.forms.main.expiry_date') }}
                        </ui-label>
                        <ui-input :disabled="true" v-model="v$.user.expiry_date.$model" />
                    </div>
                    <div class="input-container mb-2 pr-4 lg:w-1/4 md:w-1/5" data-cy="user-settings-general-role">
                        <ui-label>
                            {{ $t('user.forms.main.role') }}
                        </ui-label>
                        <ui-input :disabled="true" v-model="v$.user.roles.$model" />
                    </div>
                </div>
                <div class="section-body" v-if="isEpicvaultEnabled">
                    <div
                        class="input-container mb-2 pr-4 lg:w-1/4 md:w-1/5"
                        data-cy="user-settings-general-default-landing-page"
                    >
                        <ui-label>
                            {{ $t('user.forms.main.default_landing_page') }}
                        </ui-label>
                        <ui-select
                            :class="{ 'form-error': v$.user.default_landing_page.$error }"
                            v-model="v$.user.default_landing_page.$model"
                            :options="defaultLandinagePageList"
                            :searchable="true"
                            :allowClear="true"
                        />
                        <div
                            data-cy="field-general-default_landing_page-error"
                            :class="{ 'text-default-red': v$.user.default_landing_page.$error }"
                            v-if="v$.user.default_landing_page.required.$invalid && v$.user.default_landing_page.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors.default_landing_page" v-if="validation.errors" />
                    </div>
                </div>
                <div class="section-body">
                    <div class="input-container mb-2 lg:w-1/4 md:w-1/5" data-cy="user-settings-signature">
                        <ui-label>
                            {{ $t('user.forms.main.user_signature') }}
                            <a-tooltip placement="right" :title="$t('globals.buttons.delete')">
                                <DeleteOutlined
                                    v-if="user.user_signature"
                                    class="text-default-red"
                                    @click="handleRemove"
                                />
                            </a-tooltip>
                        </ui-label>
                        <a-upload
                            class="w-1/2 avatar-uploader"
                            list-type="picture-card"
                            :show-upload-list="false"
                            :before-upload="beforeUpload"
                        >
                            <img v-if="user.user_signature" :src="user.user_signature" alt="avatar" />
                            <div v-else>
                                <LoadingOutlined v-if="loading" />
                                <PlusOutlined v-else />
                                <div class="ant-upload-text">{{ $t('user.forms.main.upload') }}</div>
                            </div>
                        </a-upload>
                        <validation-errors :errors="validation.errors.user_signature" v-if="validation.errors" />
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSelect from '@/components/ui/Select.vue';

import ValidationErrors from '@/components/ui/ValidationErrors.vue';

import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

import formatters from '@/utils/formatters.js';
import { DeleteOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons-vue';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiInput,
        UiLabel,
        UiSelect,
        ValidationErrors,
        DeleteOutlined,
        LoadingOutlined,
        PlusOutlined,
    },
    data() {
        return {
            formatters,
            locales: [],
            timezones: [],
            loading: false,
            image_file: {},
            image_updated: false,
        };
    },
    mounted() {
        this.fetch();
    },
    computed: {
        isEpicvaultEnabled() {
            return this.$store.state.system.is_epicvault_enabled ? true : false;
        },
        defaultLandinagePageList() {
            let list = [];

            if (this.isEpicvaultEnabled) {
                if (this.$store.state.user.modules.claimsvault) {
                    list.push({
                        text: 'ClaimsVAULT Dashboard',
                        value: 'claimsvault_dashboard',
                    });

                    list.push({
                        text: 'ClaimsVault',
                        value: 'claimsvault',
                    });
                }

                if (this.$store.state.user.modules.covervault) {
                    list.push({
                        text: 'CoverVAULT Dashboard',
                        value: 'covervault_dashboard',
                    });

                    list.push({
                        text: 'CoverVault',
                        value: 'covervault',
                    });
                }
            }

            return list;
        },
    },
    props: {
        user: Object,
        processing: {
            type: Boolean,
            default: false,
        },
        validation: Object,
    },
    validations: {
        user: {
            name: {},
            email: {},
            timezone: { required },
            locale: { required },
            expiry_date: {},
            roles: {},
            default_landing_page: { required },
        },
    },
    methods: {
        isFormDirty() {
            this.$emit('is-form-dirty', this.v$.$anyDirty);
        },
        fetch() {
            this.$http
                .all([
                    this.$http.get('/api/listmanager/category/locale'),
                    this.$http.get('/api/listmanager/category/timezone'),
                ])
                .then(
                    this.$http.spread((locales, timezones) => {
                        this.locales = [];
                        this.timezones = [];

                        locales.data.data.forEach((record) => {
                            this.locales.push({
                                text: record.item,
                                value: record.value,
                            });
                        });

                        timezones.data.data.forEach((record) => {
                            this.timezones.push({
                                text: record.item,
                                value: record.value,
                            });
                        });
                    }),
                );
        },
        handleRemove(event) {
            if (this.user.user_signature != null && this.user.user_signature != '') {
                this.image_updated = true;
                this.user.user_signature = null;
            }
        },
        beforeUpload(file) {
            this.loading = true;

            this.image_file = file;
            this.image_updated = true;
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';

            if (!isJpgOrPng) {
                this.$notification.error({
                    message: this.$t('validation.mimes', { attribute: 'Logo', values: 'JPEG|PNG' }),
                });
            }

            const isLtKB = file.size / 1024 / 1024 < 2;
            if (!isLtKB) {
                this.$notification.error({
                    message: this.$t('validation.lt.file', { attribute: 'Logo', value: 2048 }),
                });
            }

            if (isJpgOrPng && isLtKB) {
                this.user.user_signature = URL.createObjectURL(file);
            }

            this.loading = false;

            return false;
        },
        async handleSubmit() {
            if (!(await this.v$.$validate())) return false;

            let user = {
                ...this.user,
            };

            const fm_data = new FormData();

            for (var key in user) {
                if (key != 'user_signature' && user[key] != null) {
                    fm_data.append(key, user[key]);
                }
            }

            if (this.image_updated) {
                if (user.user_signature != null && user.user_signature != '') {
                    fm_data.set('user_signature', this.image_file);
                } else {
                    fm_data.set('remove_logo', true);
                }
            }

            if (user.default_landing_page === undefined) {
                fm_data.set('default_landing_page', '');
            } else {
                fm_data.set('default_landing_page', user.default_landing_page);
            }

            this.image_updated = false;
            this.v$.$reset();

            this.$emit('formSubmit', {
                tab: 'General',
                data: fm_data,
            });
        },
    },
};
</script>
